import { useAuth } from '@agentnet/auth';
import { Grid } from '@material-ui/core';
import {
  getInventoryFormsApi,
  createInventoryPolicies,
  validateVirtualPoliciesApi,
  validateSingleVirtualPolicyApi,
  createFastPoliciesApi,
} from 'api/jacket-api';
import { convertFileToBase64 } from './fileUtils';
import { RegexNumeric } from 'features/constants';
import { ChangeEvent, useEffect, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import Uploader from 'ui-kit/components/uploader/Uploader';
import { AgentNetTextInput } from 'ui-kit/inputs';
import AutocompleteFirmSearch from 'ui-kit/inputs/AutocompleteFirmSearch/AutocompleteFirmSearch';
import AutocompleteFormNoSearch, { searchType } from 'ui-kit/inputs/AutocompleteFormNoSearch/AutocompleteFormNoSearch';
import StateSelectField from 'ui-kit/inputs/StateSelectField/StateSelectField';
import { FieldValidationError } from 'utilities/validation/validation';
import { firmSearchApi } from 'api/orderManagement/order-management-api';
import { FirmListType, InventoryCreationContext } from './types';
import {
  InventoryFormItemModel,
  CreateInventoryPolicyModel,
  CreateInventoryPolicyResult,
  ValidateVirtualPoliciesResult,
  CreateFastPoliciesModel,
} from 'features/jackets/types';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import RadioGroup, { RadioProps } from 'ui-kit/components/radios/RadioGroup';
import useAsync from 'hooks/useAsync';
import { APIStatus } from 'features/serviceorder/constants';

interface InventoryDrawerProps {
  openDrawer: boolean;
  setOpenDrawer: (isOpen: boolean) => void;
  onInventoryCreated?: (context: InventoryCreationContext) => void;
}

const refineSearchLabel = 'Enter at least 3 characters to refine search';

// Entry method values
const ENTRY_METHOD_QUANTITY = 'quantity';
const ENTRY_METHOD_POLICY_RANGE = 'policyRange';
const ENTRY_METHOD_SINGLE_POLICY = 'singlePolicy';
const ENTRY_METHOD_FAST_UPLOAD = 'fastUpload';

// Validation field names
const FIELD_FIRM_SEARCH_INPUT = 'firmSearchInput';
const FIELD_SELECTED_FORM = 'selectedForm';
const FIELD_QUANTITY = 'quantity';
const FIELD_SINGLE_POLICY_NUMBER = 'singlePolicyNumber';
const FIELD_START_POLICY_NUMBER = 'startPolicyNumber';
const FIELD_END_POLICY_NUMBER = 'endPolicyNumber';

const inventoryEntryMethods: RadioProps[] = [
  { label: 'Quantity', value: ENTRY_METHOD_QUANTITY, dataTestId: 'InventoryDrawer-EntryMethodQuantity' },
  {
    label: 'Policy Number Range',
    value: ENTRY_METHOD_POLICY_RANGE,
    dataTestId: 'InventoryDrawer-EntryMethodPolicyRange',
  },
  {
    label: 'Single Policy Number',
    value: ENTRY_METHOD_SINGLE_POLICY,
    dataTestId: 'InventoryDrawer-EntryMethodSinglePolicy',
  },
  { label: 'Fast Upload', value: ENTRY_METHOD_FAST_UPLOAD, dataTestId: 'InventoryDrawer-EntryMethodFastUpload' },
];

const InventoryDrawer = ({ openDrawer, setOpenDrawer, onInventoryCreated }: InventoryDrawerProps): JSX.Element => {
  const { getAccessToken } = useAuth();
  const [firmList, setFirmList] = useState<Array<FirmListType>>([]);
  const [formList, setFormList] = useState<Array<searchType>>([]);
  const [selectedForm, setSelectedForm] = useState<string | null>(null);
  const [firmSearchInput, setFirmSearchInput] = useState<string>('');
  const [myFirm, setMyFirm] = useState<FirmListType | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [noMatchesLabel, setNoMatchesLabel] = useState<string>(refineSearchLabel);
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const { addSnackbarMessage } = useSnackBars();

  const [emailNotification, setEmailNotification] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<FileWithPath[]>([]);
  const [inventoryEntryMethod, setInventoryEntryMethod] = useState<string>(ENTRY_METHOD_QUANTITY);
  const [startPolicyNumber, setStartPolicyNumber] = useState<string>('');
  const [endPolicyNumber, setEndPolicyNumber] = useState<string>('');
  const [suffix, setSuffix] = useState<string>('');
  const [singlePolicyNumber, setSinglePolicyNumber] = useState<string>('');
  const [selectedStateCode, setSelectedStateCode] = useState<string>('');

  const validateNumericEntry = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.ctrlKey ||
      e.metaKey ||
      e.altKey ||
      ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab', 'Backspace', 'Delete'].includes(e.key) ||
      RegexNumeric.test(e.key)
    ) {
      return true;
    }

    e.preventDefault();
    return false;
  };

  const clearSearch = () => {
    setFirmList([]);
    setFirmSearchInput('');
    setNoMatchesLabel(refineSearchLabel);
  };

  const handleCancel = () => {
    clearSearch();
    setFormList([]);
    setSelectedForm('');
    setQuantity(null);
    setValidationErrors([]);
    setEmailNotification('');
    setUploadedFiles([]);
    setInventoryEntryMethod(ENTRY_METHOD_QUANTITY);
    setStartPolicyNumber('');
    setEndPolicyNumber('');
    setSuffix('');
    setSinglePolicyNumber('');
    setSelectedStateCode('');
    setOpenDrawer(false);
  };

  const refreshValidationErrors = (): number => {
    const errors = [];
    if (!myFirm) {
      errors.push({ field: FIELD_FIRM_SEARCH_INPUT, message: 'Firm is required' });
    }
    if (!selectedForm && inventoryEntryMethod !== ENTRY_METHOD_FAST_UPLOAD) {
      errors.push({ field: FIELD_SELECTED_FORM, message: 'Form is required' });
    }

    if (inventoryEntryMethod === ENTRY_METHOD_QUANTITY) {
      if (!quantity || quantity <= 0) {
        errors.push({ field: FIELD_QUANTITY, message: 'Quantity must be greater than 0' });
      }
    } else if (inventoryEntryMethod === ENTRY_METHOD_POLICY_RANGE) {
      if (!startPolicyNumber) {
        errors.push({ field: FIELD_START_POLICY_NUMBER, message: 'Start Policy Number is required' });
      }
      if (!endPolicyNumber) {
        errors.push({ field: FIELD_END_POLICY_NUMBER, message: 'End Policy Number is required' });
      }
    } else if (inventoryEntryMethod === ENTRY_METHOD_SINGLE_POLICY) {
      if (!singlePolicyNumber) {
        errors.push({ field: FIELD_SINGLE_POLICY_NUMBER, message: 'Policy Number is required' });
      }
    }

    setValidationErrors(errors);

    if (errors.length > 0) {
      addSnackbarMessage({ message: 'Please fill in all required fields', type: 'error' });
    }

    return errors.length;
  };

  const spliceValidationError = (field: string) => {
    setValidationErrors(validationErrors.filter((error) => error.field !== field));
  };

  const getSelectedFirmOption = (event: number) => {
    let selectedFirm: FirmListType | undefined = undefined;
    if (firmList.length > 0) {
      selectedFirm = firmList.find((firm) => firm.value === event);
    }
    setMyFirm(selectedFirm || null);
  };

  const getInventoryForms = async (firmId: number, stateCode?: string): Promise<InventoryFormItemModel[]> => {
    const token = await getAccessToken();
    return await getInventoryFormsApi(firmId, token, stateCode);
  };

  useEffect(() => {
    const searchFirmsTimeout = setTimeout(async () => {
      if (firmSearchInput?.length > 2) {
        const result = await firmSearch(firmSearchInput);
        setFirmList(result);
      } else {
        setNoMatchesLabel(refineSearchLabel);
      }
    }, 500);
    return () => clearTimeout(searchFirmsTimeout);
  }, [firmSearchInput]);

  const firmSearch = async (input: string): Promise<Array<FirmListType>> => {
    const token = await getAccessToken();
    const response = await firmSearchApi({ searchText: input }, token);
    const firmList: Array<FirmListType> = [];
    if (Array.isArray(response.result)) {
      response.result.forEach((firm) => {
        firmList.push({
          name: firm.firmName,
          value: firm.firmId,
          display: firm.firmName + ' (' + firm.firmId + ')',
          isActive: firm.isActive,
        });
      });
    }
    if (firmList.length === 1 && input.length > 5 && !isNaN(Number(input))) {
      firmList[0].accountNumber = input;
    } else if (firmList.length === 0 && input.length > 2 && !isNaN(Number(input))) {
      setNoMatchesLabel('Enter complete account number to refine search');
    } else {
      setNoMatchesLabel('No matching firms found');
    }
    return firmList;
  };

  useEffect(() => {
    if (myFirm && myFirm.value) {
      getInventoryForms(myFirm.value, selectedStateCode || undefined).then((forms) => {
        setFormList(
          forms
            .map((form) => ({
              name: form.itemNo,
              value: form.itemNo,
              display: form.itemDescription,
              isActive: true,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      });
    } else {
      setFormList([]);
      setSelectedForm('');
    }
  }, [myFirm, selectedStateCode]);

  const validatePolicyRange = async (): Promise<ValidateVirtualPoliciesResult> => {
    const token = await getAccessToken();
    return await validateVirtualPoliciesApi(
      {
        itemNumber: selectedForm!,
        startPolicyNumber,
        endPolicyNumber,
        suffix: suffix || undefined,
        firmId: myFirm!.value,
      },
      token,
    );
  };

  const {
    execute: executeValidatePolicyRange,
    status: validatePolicyRangeStatus,
    value: validatePolicyRangeValue,
    errors: validatePolicyRangeErrors,
  } = useAsync<ValidateVirtualPoliciesResult>(validatePolicyRange, false);

  useEffect(() => {
    if (validatePolicyRangeStatus === APIStatus.success) {
      const created = validatePolicyRangeValue?.createdPolicies ?? [];
      addSnackbarMessage({
        message:
          created.length > 0
            ? `${created.length} ${created.length === 1 ? 'policy' : 'policies'} created: ${created.join(', ')}`
            : 'No policies were created',
        type: 'success',
      });
      handleCancel();
      onInventoryCreated?.({
        firmId: myFirm!.value,
        firmName: myFirm!.name,
        accountNumber: myFirm?.accountNumber,
        formNo: selectedForm ?? undefined,
      });
    }

    if (validatePolicyRangeStatus === APIStatus.error) {
      validatePolicyRangeErrors?.map((err) => {
        addSnackbarMessage({ message: err, type: 'error' });
      });
      addSnackbarMessage({ message: 'Failed to create inventory. Please try again.', type: 'error' });
    }
  }, [validatePolicyRangeStatus]);

  const validateSinglePolicy = async (): Promise<ValidateVirtualPoliciesResult> => {
    const token = await getAccessToken();
    return await validateSingleVirtualPolicyApi(
      {
        itemNumber: selectedForm!,
        policyNumber: singlePolicyNumber,
        firmId: myFirm!.value,
      },
      token,
    );
  };

  const {
    execute: executeValidateSinglePolicy,
    status: validateSinglePolicyStatus,
    value: validateSinglePolicyValue,
    errors: validateSinglePolicyErrors,
  } = useAsync<ValidateVirtualPoliciesResult>(validateSinglePolicy, false);

  useEffect(() => {
    if (validateSinglePolicyStatus === APIStatus.success) {
      const created = validateSinglePolicyValue?.createdPolicies ?? [];
      addSnackbarMessage({
        message:
          created.length > 0
            ? `${created.length} ${created.length === 1 ? 'policy' : 'policies'} created: ${created.join(', ')}`
            : 'No policies were created',
        type: 'success',
      });
      handleCancel();
      onInventoryCreated?.({
        firmId: myFirm!.value,
        firmName: myFirm!.name,
        accountNumber: myFirm?.accountNumber,
        formNo: selectedForm ?? undefined,
      });
    }

    if (validateSinglePolicyStatus === APIStatus.error) {
      validateSinglePolicyErrors?.map((err) => {
        addSnackbarMessage({ message: err, type: 'error' });
      });
      addSnackbarMessage({ message: 'Failed to create inventory. Please try again.', type: 'error' });
    }
  }, [validateSinglePolicyStatus]);

  const createFastPolicies = async (): Promise<ValidateVirtualPoliciesResult> => {
    const token = await getAccessToken();
    const fileContent = await convertFileToBase64(uploadedFiles[0]);
    const payload: CreateFastPoliciesModel = {
      firmId: myFirm!.value.toString(),
      fileContent,
    };
    return await createFastPoliciesApi(payload, token);
  };

  const {
    execute: executeCreateFastPolicies,
    status: createFastPoliciesStatus,
    value: createFastPoliciesValue,
    errors: createFastPoliciesErrors,
  } = useAsync<ValidateVirtualPoliciesResult>(createFastPolicies, false);

  useEffect(() => {
    if (createFastPoliciesStatus === APIStatus.success) {
      const created = createFastPoliciesValue?.createdPolicies ?? [];
      addSnackbarMessage({
        message:
          created.length > 0
            ? `${created.length} ${created.length === 1 ? 'policy' : 'FAST Policies'} created: ${created.join(', ')}`
            : 'No policies were created',
        type: 'success',
      });
      handleCancel();
      onInventoryCreated?.({
        firmId: myFirm!.value,
        firmName: myFirm!.name,
        accountNumber: myFirm?.accountNumber,
        formNo: undefined,
      });
    }

    if (createFastPoliciesStatus === APIStatus.error) {
      createFastPoliciesErrors?.map((err) => {
        addSnackbarMessage({ message: err, type: 'error' });
      });
      addSnackbarMessage({ message: 'Failed to create inventory. Please try again.', type: 'error' });
    }
  }, [createFastPoliciesStatus]);

  const createInventory = async (): Promise<CreateInventoryPolicyResult[]> => {
    const token = await getAccessToken();
    const payload: CreateInventoryPolicyModel[] = [
      {
        itemNumber: selectedForm!,
        policyTypeCdId: 95, // Policy
        policyStatusCdId: 9403, // 9403 = Virtual Policy Open status
        firmId: myFirm!.value,
        firmLocationId: 0, // server will assign default location, so we can send 0
        firmLocationJurisdictionId: 0, // server will assign default jurisdiction, so we can send 0
        underwriterId: 0, // server will assign underwriter, so we can send 0
        createPoliciesCount: quantity!,
      },
    ];
    return await createInventoryPolicies(payload, token);
  };

  const {
    execute: executeCreateInventory,
    status: createInventoryStatus,
    errors: createInventoryErrors,
  } = useAsync<CreateInventoryPolicyResult[]>(createInventory, false);

  useEffect(() => {
    if (createInventoryStatus === APIStatus.success) {
      const firmReference = myFirm?.accountNumber ? `Account ${myFirm.accountNumber}` : myFirm?.name;
      addSnackbarMessage({
        message: `Inventory successfully created: ${quantity} ${
          quantity === 1 ? 'policy' : 'policies'
        } generated for ${firmReference}`,
        type: 'success',
      });
      handleCancel();
      onInventoryCreated?.({
        firmId: myFirm!.value,
        firmName: myFirm!.name,
        accountNumber: myFirm?.accountNumber,
        formNo: selectedForm ?? undefined,
      });
    }

    if (createInventoryStatus === APIStatus.error) {
      createInventoryErrors?.map((err) => {
        addSnackbarMessage({ message: err, type: 'error' });
      });
      addSnackbarMessage({ message: 'Failed to create inventory. Please try again.', type: 'error' });
    }
  }, [createInventoryStatus]);

  const handleSubmit = () => {
    const errors = refreshValidationErrors();
    if (errors > 0) {
      return;
    }

    if (inventoryEntryMethod === ENTRY_METHOD_POLICY_RANGE) {
      executeValidatePolicyRange();
    } else if (inventoryEntryMethod === ENTRY_METHOD_SINGLE_POLICY) {
      executeValidateSinglePolicy();
    } else if (inventoryEntryMethod === ENTRY_METHOD_FAST_UPLOAD) {
      executeCreateFastPolicies();
    } else {
      executeCreateInventory();
    }
  };

  const isSubmitting =
    validatePolicyRangeStatus === APIStatus.pending ||
    validateSinglePolicyStatus === APIStatus.pending ||
    createFastPoliciesStatus === APIStatus.pending ||
    createInventoryStatus === APIStatus.pending;

  const isAddInventoryDisabled =
    isSubmitting || (inventoryEntryMethod === ENTRY_METHOD_FAST_UPLOAD && (uploadedFiles.length === 0 || !myFirm));

  return (
    <FormDrawerComponent
      title="Add New Inventory"
      open={openDrawer}
      crossQAPrefix="InventoryDrawer-"
      primaryActionProps={{
        loading: isSubmitting,
        disabled: isAddInventoryDisabled,
      }}
      dismissActionProps={{ disabled: isSubmitting }}
      primaryActionLabel={isSubmitting ? 'Creating...' : 'Add Inventory'}
      onPrimaryAction={handleSubmit}
      onDismissAction={handleCancel}
      testId="inventory-drawer"
      width={720}
    >
      <Grid container spacing={3} className="inventory-drawer-form-container">
        <Grid item xs={12} className="firm-search-form">
          <AutocompleteFirmSearch
            label="Firm Name/Account No."
            name="firmSearchInput"
            data-testid="InventoryDrawer-FirmSearch"
            required={true}
            hideSearchIcon
            shouldDisableClearable={!firmSearchInput}
            onChange={(event) => {
              const value = (event.target as HTMLInputElement)?.value;
              setFirmSearchInput(value);
              getSelectedFirmOption(event);
            }}
            onBlur={(event) => {
              const value = (event.target as HTMLInputElement)?.value;
              if (value) {
                spliceValidationError(FIELD_FIRM_SEARCH_INPUT);
              }
              clearSearch();
              setFirmSearchInput(value);
            }}
            options={firmList}
            noMatchesLabel={noMatchesLabel}
            error={validationErrors?.some((err) => err.field === FIELD_FIRM_SEARCH_INPUT)}
            errs={validationErrors}
            helperText={validationErrors?.some((err) => err.field === FIELD_FIRM_SEARCH_INPUT) && 'Firm is required'}
            showValidation={true}
          />
        </Grid>
        <Grid item xs={4}>
          <StateSelectField
            label="State"
            name="stateCode"
            data-testid="InventoryDrawer-StateCode"
            value={selectedStateCode}
            onChange={(value: string | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              setSelectedStateCode(value as string);
              setSelectedForm('');
            }}
            shouldDisableClearable={!selectedStateCode}
            disabled={formList.length === 0 || inventoryEntryMethod === ENTRY_METHOD_FAST_UPLOAD}
          />
        </Grid>
        <Grid item xs={8} className="firm-search-form">
          <AutocompleteFormNoSearch
            label="Form No."
            name="selectedForm"
            data-testid="InventoryDrawer-FormSearch"
            required={true}
            shouldDisableClearable={!selectedForm}
            hideSearchIcon
            value={selectedForm || undefined}
            onBlur={(event) => {
              const value = (event.target as HTMLInputElement)?.value;
              if (value) {
                spliceValidationError(FIELD_SELECTED_FORM);
              }
              setSelectedForm(value);
            }}
            options={formList}
            disabled={formList.length === 0 || inventoryEntryMethod === ENTRY_METHOD_FAST_UPLOAD}
            hideNoMatches={true}
            noMatchesLabel="No matching forms found"
            error={validationErrors?.some((err) => err.field === FIELD_SELECTED_FORM)}
            errs={validationErrors}
            helperText={validationErrors?.some((err) => err.field === FIELD_SELECTED_FORM) && 'Form is required'}
            showValidation={true}
          />
        </Grid>
        <Grid item xs={12}>
          <AgentNetTextInput
            label="Email Notification"
            data-testid="InventoryDrawer-EmailNotification"
            onChange={(e) => setEmailNotification(e.target.value)}
            fullWidth
            value={emailNotification}
          />
        </Grid>
        <Grid item xs={12}>
          <RadioGroup
            label="Inventory Entry Method"
            selected={inventoryEntryMethods.find((m) => m.value === inventoryEntryMethod)}
            options={inventoryEntryMethods}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setInventoryEntryMethod(e.target.value);
              setValidationErrors([]);
              if (e.target.value === ENTRY_METHOD_FAST_UPLOAD) {
                setSelectedStateCode('');
                setSelectedForm('');
              }
            }}
            compact
          />
        </Grid>
        {inventoryEntryMethod === ENTRY_METHOD_FAST_UPLOAD && (
          <Grid item xs={12}>
            <Uploader
              handleFiles={(files) => setUploadedFiles((prev) => [...prev, ...files])}
              onDeleteItem={(file) => setUploadedFiles((prev) => prev.filter((f) => f !== file))}
              listTitle="Uploaded Document"
              maxSize={10}
              acceptTypes={{
                'application/vnd.ms-excel': ['.xls'],
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
              }}
              drawerMode
              data-testid={'InventoryDrawer-FastUpload'}
            />
          </Grid>
        )}
        {inventoryEntryMethod === ENTRY_METHOD_QUANTITY && (
          <Grid item xs={6}>
            <AgentNetTextInput
              onKeyDown={validateNumericEntry}
              label="Quantity"
              data-testid="InventoryDrawer-Quantity"
              onChange={(value) => {
                if (!value) {
                  setQuantity(null);
                  return;
                }
                const quantity = Number(value.target.value);
                setQuantity(quantity > 0 ? quantity : null);
                spliceValidationError(FIELD_QUANTITY);
              }}
              fullWidth
              value={quantity?.toString() || undefined}
              helperText={
                validationErrors?.some((err) => err.field === FIELD_QUANTITY) ? 'Quantity must be greater than 0' : ''
              }
              error={validationErrors?.some((err) => err.field === FIELD_QUANTITY)}
            />
          </Grid>
        )}
        {inventoryEntryMethod === ENTRY_METHOD_SINGLE_POLICY && (
          <Grid item xs={6}>
            <AgentNetTextInput
              label="Policy Number"
              data-testid="InventoryDrawer-SinglePolicyNumber"
              required
              onChange={(e) => {
                setSinglePolicyNumber(e.target.value);
                if (e.target.value) spliceValidationError(FIELD_SINGLE_POLICY_NUMBER);
              }}
              fullWidth
              value={singlePolicyNumber}
              helperText={
                validationErrors?.some((err) => err.field === FIELD_SINGLE_POLICY_NUMBER)
                  ? 'Policy Number is required'
                  : ''
              }
              error={validationErrors?.some((err) => err.field === FIELD_SINGLE_POLICY_NUMBER)}
            />
          </Grid>
        )}
        {inventoryEntryMethod === ENTRY_METHOD_POLICY_RANGE && (
          <>
            <Grid item xs={6}>
              <AgentNetTextInput
                label="Start Policy Number"
                data-testid="InventoryDrawer-StartPolicyNumber"
                required
                onChange={(e) => {
                  setStartPolicyNumber(e.target.value);
                  if (e.target.value) spliceValidationError(FIELD_START_POLICY_NUMBER);
                }}
                fullWidth
                value={startPolicyNumber}
                helperText={
                  validationErrors?.some((err) => err.field === FIELD_START_POLICY_NUMBER)
                    ? 'Start Policy Number is required'
                    : ''
                }
                error={validationErrors?.some((err) => err.field === FIELD_START_POLICY_NUMBER)}
              />
            </Grid>
            <Grid item xs={6}>
              <AgentNetTextInput
                label="End Policy Number"
                data-testid="InventoryDrawer-EndPolicyNumber"
                required
                onChange={(e) => {
                  setEndPolicyNumber(e.target.value);
                  if (e.target.value) spliceValidationError(FIELD_END_POLICY_NUMBER);
                }}
                fullWidth
                value={endPolicyNumber}
                helperText={
                  validationErrors?.some((err) => err.field === FIELD_END_POLICY_NUMBER)
                    ? 'End Policy Number is required'
                    : ''
                }
                error={validationErrors?.some((err) => err.field === FIELD_END_POLICY_NUMBER)}
              />
            </Grid>
            <Grid item xs={12}>
              <AgentNetTextInput
                label="Suffix"
                data-testid="InventoryDrawer-Suffix"
                onChange={(e) => setSuffix(e.target.value)}
                fullWidth
                value={suffix}
              />
            </Grid>
          </>
        )}
      </Grid>
    </FormDrawerComponent>
  );
};

export default InventoryDrawer;
